//Imports
import React, { useCallback, useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { useInput, useNewEntityTag } from "Core/hooks";
import TagsManage from 'Modules/.common/components/TagsManage';

//Actions
import {
  manageCreateEditManageEquipment,
  manageGetManageEquipment,
  reset,
} from "./redux/slice";

//Controls
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
} from "@gpstrackit/gtc-react-controls";

//Components
import {
  NotificationManager,
  NotificationContainer,
} from "Components/Notifications";

import { getRequiredTrimmedNonEmptyRules } from "Modules/.common/utils/gtcFormItemRules";

const CreateEditManageEquipment = ({ refresh }) => {
  //States
  const [formData, setFormData] = useState({ vehicles: [], name: null });

  //Language
  const { messages } = useIntl();

  //Form refs
  const createEditEquipmentFormRef = Form.useForm();

  //Redux
  const dispatch = useDispatch();
  const {
    equipmentSelected,
    equipment,
    loading,
    error,
    updateManageEquipmentResult,
    open,
    drivers,
  } = useSelector((state) => state.manageCreateEditManageEquipmentRedux);

  //Memos
  const title = useMemo(
    () =>
      equipment?.name
        ? `${messages["manage_createEditEquipment_EditTitle"]}: "${equipment?.name}"`
        : messages["manage_createEditEquipment_CreateTitle"],
    [equipment]
  );

  const nameFieldRules = useMemo(
    () => getRequiredTrimmedNonEmptyRules(messages["required_field"]),
    [messages]
  );

  //Handlers and utils
  const handleUpdateManageEquipmentResult = useCallback(() => {
    const name = formData.name != null ? String(formData.name).trim() : "";
    if (!name) return;
    const { vehicles } = formData;
    dispatch(
      manageCreateEditManageEquipment({
        name,
        units: vehicles.map(vehicle => vehicle.id),
        id: equipment?.id,
      })
    );
  }, [formData, equipment]);

  const handleClose = useCallback(() => {
    dispatch(reset());
  }, []);

  const handleSuccess = useCallback(() => {
    NotificationManager.success(
      equipment?.id ? messages["manage_createEditEquipment_success_edit"] : messages["manage_createEditEquipment_success"],
      messages["success"],
      3000,
      null,
      null,
      "success"
    );
    refresh();
    handleClose();
  }, [equipment]);

  //Effects
  useEffect(() => {
    if (!open || !equipmentSelected) return;
    dispatch(manageGetManageEquipment({ id: equipmentSelected.id }));
  }, [open, equipmentSelected]);

  useEffect(() => {
    if (!updateManageEquipmentResult?.id) return;
    handleSuccess();
  }, [updateManageEquipmentResult]);

  useEffect(() => {
    if (!error) return;
    let errorMessage = error?.error || messages["errorMessage"];
    if (
      error?.errorData?.errors?.find((err) => err?.errorInfo?.code === 1010)
    ) {
      errorMessage =
        messages["manage_createEditEquipment_duplicatedEquipmentErrorMessage"];
    }
    NotificationManager.error(
      errorMessage,
      messages["error"],
      4000,
      null,
      null,
      "error"
    );
    dispatch(reset({ error: null }));
  }, [error]);

  useEffect(() => {
    if (!open || !equipment) return;
    const { name, vehicles } = equipment;
    const defaultData = {
      name,
      vehicles: vehicles.map((vehicle) => ({ ...vehicle.unit, name: "Unit" })),
    };
    setFormData(defaultData);
    createEditEquipmentFormRef.setValues(defaultData);
  }, [equipment, open, drivers]);

  useEffect(() => {
    const noEmojisRegex = /[\uD83C-\uDBFF\uDC00-\uDFFF]+/g;
    const value = formData.name
    if (!value) return
    if (!noEmojisRegex.test(formData.name)) return
    const noEmojis = value.replace(noEmojisRegex, "");
    const newFormData = {...formData, name: noEmojis}
    setFormData(newFormData)
    createEditEquipmentFormRef.setValues(newFormData);
  }, [formData])

  return (
    <Modal
      title={
        title && title.length > 48 ? title.substring(0, 45) + '..."' : title
      }
      open={open}
      handleClose={handleClose}
      position="right"
      style={{ zIndex: "auto" }}
      size="small"
      loading={loading}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            isDisabled={loading}
            style={{ marginRight: 8 }}
            onClick={handleClose}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>

          <Button
            isDisabled={loading}
            onClick={() => {
              createEditEquipmentFormRef?.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        <Form
          ref={createEditEquipmentFormRef.ref}
          loading={loading}
          onValidSubmit={() => handleUpdateManageEquipmentResult()}
          onChangeValues={() => {}}
        >
          <Form.Item
            name="name"
            label={messages["manage_createEditEquipment_NameInputLabel"]}
            rules={nameFieldRules}
          >
            <Input
              id="name"
              type="text"
              placeholder={
                messages["manage_createEditEquipment_NameInputLabel"]
              }
              onChange={(value) => setFormData({ ...formData, name: value })}
              maxLength={20}
              isDisabled={equipment?.equipmentModelId === 2}
            />
          </Form.Item>
          <TagsManage
            entities={[
              { id: "Unit", label: messages["unit_value"] }
            ]}
            value={formData?.vehicles || []}
            mode="list"
            height={formData?.vehicles?.length > 0 ? 500 : 600}
            onChange={(newItems) => {
              setFormData({ ...formData, vehicles: newItems });
            }}
            entitySelected={{ id: "Unit", label: messages["unit_value"] }}
            menuStyle={{ maxHeight: '280px', maxWidth: '417px' }}
            isVisiblePill={true}
          />
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default CreateEditManageEquipment;
